import React from 'react';
import { IStorefrontBanner, IStorefrontPage } from '../../../../models/Storefront';
import { ActionButton, Dropdown, IDropdownOption, Panel, PanelType, Stack, TextField, Toggle } from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import StorefrontBannerImageUploader from './StorefrontBannerImageUploader';
import { bannersForPage, IHeroBannerOptions, isOnPage, mergeReorderedSubset, sortedPages } from '../pagesConfig';
import {
    advancedReplacementPatch,
    AdvancedStyleButton,
    ElementPropertyRows,
    parseNum,
    StyleGroupAccordion,
    StyleOverridesGroup,
} from '../styleControls';
import { ColorRow, StyleRow, useCellControlStyles } from '../styleRows';
import { STYLE_PART_ICONS, styleTargetForKey } from '../stylesConfig';
import { ISearchElementStyle, ISearchTextStyle } from '../searchPageConfig';
import StorefrontSectionAdvancedStyleEditorModal from '../../StorefrontSectionAdvancedStyleEditorModal';
import {
    BannerOverlayHorizontal,
    BannerOverlayVertical,
    IBannerOverlayButton,
    IBannerOverlayConfig,
    IBannerOverlayPosition,
    parseBannerOverlayConfig,
    serializeBannerOverlayConfig,
} from '../bannerOverlayConfig';
import Setting, { DynamicSetting } from '../../../Common/Setting';
import ConditionalSetting from '../../../Common/ConditionalSetting';

const OVERLAY_HORIZONTAL_OPTIONS: IDropdownOption[] = [
    { key: 'left', text: 'Left' },
    { key: 'center', text: 'Center' },
    { key: 'right', text: 'Right' },
];

const OVERLAY_VERTICAL_OPTIONS: IDropdownOption[] = [
    { key: 'top', text: 'Top' },
    { key: 'middle', text: 'Middle' },
    { key: 'bottom', text: 'Bottom' },
];

const OVERLAY_TEXT_ALIGN_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Follow horizontal' },
    { key: 'left', text: 'Left' },
    { key: 'center', text: 'Center' },
    { key: 'right', text: 'Right' },
];

const IMAGE_FIT_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Default' },
    { key: 'fit', text: 'Fit (letterbox)' },
    { key: 'fill', text: 'Fill (crop)' },
];

const ACCENT = 'rgb(120, 156, 47)';

// ISearchTextStyle is narrower than the ISearchElementStyle the generic rows speak, so the dicts
// don't structurally interchange in either direction, even though at RUNTIME they are all open
// camelCase CSS bags with an index signature. One loose cast at the boundary, in both directions,
// exactly as CategoryPodStylePanel and BrowseAllStylePanel do it.
const loose = (dict?: { [prop: string]: any }): any => dict;

interface IStorefrontBannerEditorPanelProps {
    banners: IStorefrontBanner[];
    onChange: (banners: IStorefrontBanner[]) => void;
    height?: number;
    onHeightChange: (height?: number) => void;
    width?: number;
    isOpen: boolean;
    onDismiss: () => void;
    ignoreGlobalBanners?: boolean;
    onIgnoreGlobalBannersChange?: (value: boolean) => void;
    // Selection is owned by the parent editor so the preview carousel can follow it
    // (WYSIWYG): selecting a thumbnail here shows that slide behind the non-blocking panel.
    selectedBannerId: string | null;
    onSelectBanner: (id: string | null) => void;
    // Custom Page editing context (a page GUID). Undefined/empty = the main homepage. The thumbnail
    // strip shows only this page's banners; a new banner is stamped with it; reordering merges back
    // into the full list so other pages' banners are preserved on batch-save.
    page?: string;
    // The storefront's custom pages — move targets for "Move to Page". Absent/empty hides the move
    // button (nothing to move to besides the current page).
    pages?: IStorefrontPage[];
    // Hero banner presentation for the page being edited (main homepage → homePageConfig fields;
    // custom page → its pagesConfig entry). Undefined hides the hero controls.
    heroOptions?: IHeroBannerOptions;
    onHeroOptionsChange?: (patch: Partial<IHeroBannerOptions>) => void;
    // homePageConfig.bannerRotationSeconds — dwell seconds per slide, applied store-wide (every
    // page's carousel). The control renders only when the handler is supplied — the main homepage —
    // since a custom-page panel would be editing a global value behind the merchant's back.
    rotationSeconds?: number;
    onRotationSecondsChange?: (seconds?: number) => void;
}

const StorefrontBannerEditorPanel: React.FC<IStorefrontBannerEditorPanelProps> = (props) => {
    // Destructured so the presence checks narrow types inside the callbacks below (the hero controls
    // and the global-banners toggle are each rendered only when their handler was supplied).
    const { heroOptions, onHeroOptionsChange, onIgnoreGlobalBannersChange, onRotationSecondsChange } = props;
    const cell = useCellControlStyles();
    // Thumbnail strip sized to fit ~3 tiles per row inside the 420px panel; height keeps the
    // banner aspect ratio (same math the old modal used, just a narrower base width).
    const thumbWidth = 108;
    // 350 matches the preview carousel's default height so thumbnails keep the same aspect
    // ratio as the preview when no banner height is configured.
    const [thumbHeight, setThumbHeight] = React.useState((props.height ?? 350) / (props.width ?? 800) * thumbWidth);
    const [dragIndex, setDragIndex] = React.useState<number | null>(null);
    const [dropIndex, setDropIndex] = React.useState<number | null>(null);
    // Plain local state: this group is the page's carousel-wide settings, entirely independent of
    // the one-open-at-a-time accordions BannerDetail runs over the SELECTED banner's parts.
    const [carouselOpen, setCarouselOpen] = React.useState(false);

    React.useEffect(() => {
        setThumbHeight((props.height ?? 350) / (props.width ?? 800) * thumbWidth);
    }, [props.height, props.width]);

    const onDragStart = (index: number) => {
        setDragIndex(index);
        setDropIndex(index);
    };

    // Banners shown in the strip: this page's only. Drag indices below are indices into this subset.
    const pageBanners = bannersForPage(props.banners, props.page);

    const onDrop = (index: number) => {
        if (dragIndex === null || dragIndex === index) {
            setDragIndex(null);
            setDropIndex(null);
            return;
        }

        // Reorder within the page subset, then merge back into the full list so the other pages'
        // banners keep their positions (and aren't dropped by the delete-all-then-re-add batch save).
        const reorderedSubset = [...pageBanners];
        const [moved] = reorderedSubset.splice(dragIndex, 1);
        reorderedSubset.splice(index, 0, moved);
        props.onChange(mergeReorderedSubset(props.banners, (b) => isOnPage(b.page, props.page), reorderedSubset));
        setDragIndex(null);
        setDropIndex(null);
    };

    const onDragEnd = () => {
        setDragIndex(null);
        setDropIndex(null);
    };

    const onNewImage = (value: string) => {
        const newBanner: IStorefrontBanner = {
            id: `new-${Date.now()}`,
            imageUrl: value,
            page: props.page || undefined,
        };
        props.onChange([...props.banners, newBanner]);
        // A newly-added banner becomes the selected one so the user drops straight into editing it.
        props.onSelectBanner(newBanner.id ?? null);
    };

    const selectedBanner = props.banners.find((b) => b.id === props.selectedBannerId) ?? null;
    const selectedIndex = props.banners.findIndex((b) => b.id === props.selectedBannerId);

    const updateSelected = (updated: IStorefrontBanner) => {
        if (selectedIndex < 0) return;
        const updatedBanners = [...props.banners];
        updatedBanners[selectedIndex] = updated;
        props.onChange(updatedBanners);
    };

    const deleteSelected = () => {
        if (selectedIndex < 0) return;
        const updatedBanners = [...props.banners];
        updatedBanners.splice(selectedIndex, 1);
        props.onChange(updatedBanners);
        props.onSelectBanner(null);
    };

    // Move targets: the main homepage plus every custom page except the one being edited. Key ''
    // = main homepage (banners with no `page` belong to it).
    const moveTargets: IDropdownOption[] = [
        ...(props.page ? [{ key: '', text: 'Main Home Page' }] : []),
        ...sortedPages(props.pages ?? [])
            .flatMap((p) => (p.id && p.id !== props.page ? [{ key: p.id, text: p.title || p.slug }] : [])),
    ];

    // Re-stamp the selected banner's page and deselect — it leaves this page's thumbnail strip, and
    // the full list keeps it so it shows up when that page is edited (and survives batch-save).
    const moveSelected = (targetPageId?: string) => {
        if (selectedIndex < 0) return;
        const updatedBanners = [...props.banners];
        updatedBanners[selectedIndex] = { ...updatedBanners[selectedIndex], page: targetPageId || undefined };
        props.onChange(updatedBanners);
        props.onSelectBanner(null);
    };

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Edit Banners'
            // Non-blocking so the live preview carousel behind the panel stays visible and
            // updates as edits are made (WYSIWYG). The overflowX guard keeps the wider controls
            // (the 372px uploader) from pushing a horizontal scrollbar into the panel.
            type={PanelType.custom}
            customWidth='420px'
            styles={{ scrollableContent: { overflowX: 'hidden' } }}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            {/* Deliberately no sticky example frame here, unlike the card / pod / Browse All panels:
                this panel is non-blocking and the preview carousel behind it follows the thumbnail
                selection, so the canvas already IS the live example of the slide being edited. */}

            {/* The slides come first: picking, adding and reordering banners is this panel's primary
                object, and everything below it edits whichever one is selected. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {pageBanners.map((banner, index) => {
                    const isSelected = banner.id === props.selectedBannerId;
                    const isDropTarget = dropIndex === index && dragIndex !== null && dragIndex !== index;
                    return (
                        <div
                            key={banner.id || `${banner.name || 'banner-thumb'}-${index}`}
                            draggable
                            onDragStart={() => onDragStart(index)}
                            onDragEnter={() => setDropIndex(index)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => onDrop(index)}
                            onDragEnd={onDragEnd}
                            onClick={() => props.onSelectBanner(banner.id ?? null)}
                            title={banner.name || banner.altText || `Banner ${index + 1}`}
                            style={{
                                width: thumbWidth,
                                height: thumbHeight,
                                borderRadius: 8,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                position: 'relative',
                                boxSizing: 'border-box',
                                border: `2px solid ${isSelected ? ACCENT : 'transparent'}`,
                                boxShadow: isDropTarget ? '0 0 0 3px rgba(120, 156, 47, 0.5)' : (isSelected ? undefined : '0 0 0 1px #d0d0d0'),
                                opacity: dragIndex === index ? 0.6 : (banner.disabled ? 0.5 : 1),
                            }}
                        >
                            {banner.imageUrl ? (
                                <img
                                    src={banner.imageUrl}
                                    alt={banner.altText || banner.name || `Banner ${index + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                                />
                            ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#605e5c', background: '#f3f5f7', textAlign: 'center', padding: 4, boxSizing: 'border-box' }}>
                                    {banner.name || `Banner ${index + 1}`}
                                </div>
                            )}
                        </div>
                    );
                })}
                <div
                    style={{
                        width: thumbWidth,
                        height: thumbHeight,
                        border: `2px dashed ${ACCENT}`,
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                    }}
                >
                    <StorefrontBannerImageUploader
                        isNew
                        onChange={onNewImage}
                        width={thumbWidth}
                        height={thumbHeight}
                    />
                </div>
            </div>

            {/* Everything that applies to the banner AREA rather than to one slide, in a single
                collapsed group: it is set once per page and then left alone, so it shouldn't stand
                between the merchant and the slide they came here to edit. */}
            <StyleGroupAccordion
                label='Carousel Settings'
                iconName='Settings'
                tooltip='Settings for the whole banner area of this page, not for one slide: height, how the images fill their box, and whether the hero runs full width.'
                expanded={carouselOpen}
                onToggle={setCarouselOpen}
            >
                {onIgnoreGlobalBannersChange && (
                    <Setting id='storefront.banners.ignoreGlobalBanners'>
                        <Toggle
                            label='Ignore Global Banners'
                            inlineLabel
                            onText='On'
                            offText='Off'
                            checked={props.ignoreGlobalBanners ?? false}
                            onChange={(_, checked) => onIgnoreGlobalBannersChange(checked ?? false)}
                        />
                    </Setting>
                )}
                <Setting id='storefront.banners.height'>
                    <StyleRow label='Height'>
                        {/* Empty clears the storefront-level override, so a single banner falls back to
                            its image's natural height (carousels use 350px). */}
                        <TextField
                            type='number'
                            min={0}
                            suffix='px'
                            value={props.height?.toString() ?? ''}
                            onChange={(_, v) => props.onHeightChange(parseNum(v))}
                            styles={cell.textField}
                        />
                    </StyleRow>
                </Setting>
                <div style={{ fontSize: 11, color: '#605e5c' }}>
                    Leave empty to use the image&apos;s natural height for a single banner; carousels default to 350px.
                </div>

                {onRotationSecondsChange && <>
                    <Setting id='storefront.banners.rotationSeconds'>
                        <StyleRow label='Rotation seconds'>
                            {/* Empty clears the override back to the live store's 7s default. */}
                            <TextField
                                type='number'
                                min={0}
                                suffix='s'
                                placeholder='7'
                                value={props.rotationSeconds?.toString() ?? ''}
                                onChange={(_, v) => onRotationSecondsChange(parseNum(v))}
                                styles={cell.textField}
                            />
                        </StyleRow>
                    </Setting>
                    <div style={{ fontSize: 11, color: '#605e5c' }}>
                        Seconds each slide shows before the carousel advances, on every page of this store.
                        0 turns auto-advance off; the live store holds values to a 2 second minimum.
                    </div>
                </>}

                {/* Hero presentation for this page. These are the same fields the Home Page Style
                    bar used to duplicate; they live here alone now. */}
                {heroOptions && onHeroOptionsChange && <>
                    <Setting id='storefront.banners.fullWidth'>
                        <Toggle
                            label='Full page width'
                            inlineLabel
                            onText='Full width'
                            offText='Page width'
                            checked={!!heroOptions.bannerFullWidth}
                            onChange={(_, checked) => onHeroOptionsChange({ bannerFullWidth: checked || undefined })}
                        />
                    </Setting>
                    <Setting id='storefront.banners.imageFit'>
                        <StyleRow label='Image fit'>
                            <Dropdown
                                options={IMAGE_FIT_OPTIONS}
                                selectedKey={heroOptions.bannerImageFit ?? ''}
                                onChange={(_, o) => onHeroOptionsChange({ bannerImageFit: (o?.key as 'fit' | 'fill') || undefined })}
                                styles={cell.dropdown}
                            />
                        </StyleRow>
                    </Setting>
                    <ConditionalSetting when={!!heroOptions.bannerFullWidth} id='storefront.banners.underHeader'>
                        <Setting id='storefront.banners.underHeader'>
                            <Toggle
                                label='Under header'
                                title='Desktop only on the live store'
                                inlineLabel
                                checked={!!heroOptions.bannerUnderHeader}
                                onChange={(_, checked) => onHeroOptionsChange({ bannerUnderHeader: checked || undefined })}
                            />
                        </Setting>
                    </ConditionalSetting>
                    {/* Only meaningful while the header actually floats over the hero: these two
                        style that floating bar in its not-yet-scrolled state. */}
                    <ConditionalSetting
                        when={!!heroOptions.bannerFullWidth && !!heroOptions.bannerUnderHeader}
                        ids={['storefront.banners.overlayBarColor', 'storefront.banners.overlayBarShadow']}
                    >
                        <Setting id='storefront.banners.overlayBarColor'>
                            <ColorRow
                                label='Overlay bar color'
                                alpha
                                value={heroOptions.bannerUnderHeaderBackground}
                                onChange={(v) => onHeroOptionsChange({ bannerUnderHeaderBackground: v })}
                            />
                        </Setting>
                        <Setting id='storefront.banners.overlayBarShadow'>
                            <StyleRow label='Overlay bar shadow'>
                                <TextField
                                    title='Box shadow under the floating bar before scrolling'
                                    placeholder='e.g. 0 2px 12px rgba(0,0,0,0.25)'
                                    value={heroOptions.bannerUnderHeaderShadow ?? ''}
                                    onChange={(_, v) => onHeroOptionsChange({ bannerUnderHeaderShadow: v || undefined })}
                                    styles={cell.textField}
                                />
                            </StyleRow>
                        </Setting>
                    </ConditionalSetting>
                </>}
            </StyleGroupAccordion>

            {selectedBanner ? (
                // Remount on selection change so the per-banner local overlay state resets.
                <BannerDetail
                    key={selectedBanner.id}
                    banner={selectedBanner}
                    onChange={updateSelected}
                    onDelete={deleteSelected}
                    moveTargets={moveTargets}
                    onMove={moveSelected}
                />
            ) : (
                <div style={{ marginTop: 24, color: '#605e5c', fontSize: 13 }}>
                    Select a banner above to edit it, or use the dashed tile to add a new one.
                </div>
            )}
        </Panel>
    );
};

// Exported so content "banner" sections can reuse the full overlay editor (Banner / Title /
// Subtitle / Button / Box / Position groups): their per-entry shape is IStorefrontBanner-compatible.
// That host is a 460px section panel, so the flat rows below deliberately stay on the default
// 180px control column (no StyleRowsWidthProvider) and read the same in both.
export interface IBannerDetailProps {
    banner: IStorefrontBanner;
    onChange: (updated: IStorefrontBanner) => void;
    onDelete: () => void;
    // Optional "Move to Page" support (the home/custom-page banner panel only — content sections
    // aren't page-scoped and omit these). Empty/absent targets hide the button.
    moveTargets?: IDropdownOption[];
    onMove?: (pageId?: string) => void;
}

export const BannerDetail: React.FC<IBannerDetailProps> = (props) => {
    // Destructured so the presence check narrows types inside the move menu's callbacks.
    const { moveTargets, onMove } = props;
    const cell = useCellControlStyles();
    // Local copy of the parsed overlay config: style/position edits made before any text
    // is entered would otherwise be dropped (serializeBannerOverlayConfig returns undefined
    // while the overlay has no visible content) and snap back in the controls. This component
    // is remounted (keyed on banner id) whenever the selection changes so this stays per-banner.
    const [overlay, setOverlay] = React.useState<IBannerOverlayConfig>(() => parseBannerOverlayConfig(props.banner.overlayConfig));

    const updateOverlay = (patch: Partial<IBannerOverlayConfig>) => {
        const next = { ...overlay, ...patch };
        setOverlay(next);
        props.onChange({ ...props.banner, overlayConfig: serializeBannerOverlayConfig(next) });
    };
    const updateTitleStyle = (patch: Partial<ISearchTextStyle>) => updateOverlay({ titleStyle: { ...overlay.titleStyle, ...patch } });
    const updateSubtitleStyle = (patch: Partial<ISearchTextStyle>) => updateOverlay({ subtitleStyle: { ...overlay.subtitleStyle, ...patch } });
    const updateButton = (patch: Partial<IBannerOverlayButton>) => updateOverlay({ button: { ...overlay.button, ...patch } });
    const updateButtonStyle = (patch: Partial<ISearchElementStyle>) => updateButton({ style: { ...overlay.button?.style, ...patch } });
    const updateBoxStyle = (patch: Partial<ISearchElementStyle>) => updateOverlay({ box: { ...overlay.box, ...patch } });
    const updatePosition = (patch: Partial<IBannerOverlayPosition>) => updateOverlay({ position: { ...overlay.position, ...patch } });

    // One group open at a time, seeded on the slide itself (the image is what a merchant reaches for
    // first), the same accordion behaviour as the pod / Browse All panels, so this reads as a short
    // scannable list rather than six full property sets stacked end to end. It replaces the Pivot
    // this editor used to be, which hid five sixths of the surface behind a tab click.
    const [openGroup, setOpenGroup] = React.useState('banner');
    const group = (key: string) => ({
        expanded: openGroup === key,
        onToggle: (expanded: boolean) => setOpenGroup(expanded ? key : ''),
    });

    // The raw key/value CSS editor, one entry per style dict. It hands back the WHOLE dict rather
    // than a patch (a key deleted there has to disappear), so each entry replaces its dict outright
    // instead of merging. The update* helpers above stay the merge path for the flat rows.
    const [advancedGroup, setAdvancedGroup] = React.useState<string | undefined>();
    const advanced: Record<string, { value?: Record<string, any>; replace: (css: Css) => void }> = {
        title: { value: overlay.titleStyle, replace: (css) => updateTitleStyle(advancedReplacementPatch(overlay.titleStyle, css)) },
        subtitle: { value: overlay.subtitleStyle, replace: (css) => updateSubtitleStyle(advancedReplacementPatch(overlay.subtitleStyle, css)) },
        button: { value: overlay.button?.style, replace: (css) => updateButtonStyle(advancedReplacementPatch(overlay.button?.style, css)) },
        box: { value: overlay.box, replace: (css) => updateBoxStyle(advancedReplacementPatch(overlay.box, css)) },
    };

    return (
        <>
        <StyleGroupAccordion
            label='Banner'
            iconName={STYLE_PART_ICONS.image}
            tooltip='The slide itself: its image, alt text, link, and whether it shows.'
            {...group('banner')}
        >
            <Stack tokens={{ childrenGap: 8 }}>
                {/* 372px is the widest the 420px panel affords; the section panel that also hosts
                    this component is wider still, so it fits there too. */}
                <StorefrontBannerImageUploader
                    value={props.banner.imageUrl}
                    imageId={props.banner.id}
                    onChange={(newUrl) => props.onChange({ ...props.banner, imageUrl: newUrl })}
                    width={372}
                    height={90}
                />
                <DynamicSetting id={`storefront.banner.${props.banner.id}.altText`}>
                    <TextField
                        label='Alt Text'
                        value={props.banner.altText}
                        onChange={(_, newValue) => props.onChange({ ...props.banner, altText: newValue ?? '' })}
                    />
                </DynamicSetting>
                <DynamicSetting id={`storefront.banner.${props.banner.id}.linkUrl`}>
                    <TextField
                        label='Link URL'
                        value={props.banner.linkUrl}
                        onChange={(_, newValue) => props.onChange({ ...props.banner, linkUrl: newValue ?? '' })}
                    />
                </DynamicSetting>
                <DynamicSetting id={`storefront.banner.${props.banner.id}.enabled`}>
                    <Toggle
                        label='Enabled'
                        inlineLabel
                        onText='On'
                        offText='Off'
                        checked={!props.banner.disabled}
                        onChange={(_, checked) => props.onChange({ ...props.banner, disabled: !(checked ?? true) })}
                    />
                </DynamicSetting>
                <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginTop: 8 }}>
                    <ActionButton
                        iconProps={{ iconName: 'Delete' }}
                        onClick={props.onDelete}
                    >
                        Remove Banner
                    </ActionButton>
                    {onMove && moveTargets && moveTargets.length > 0 && (
                        <ActionButton
                            iconProps={{ iconName: 'MoveToFolder' }}
                            title='Move this banner to another page'
                            menuProps={{
                                items: moveTargets.map((t) => ({
                                    key: String(t.key),
                                    text: t.text,
                                    onClick: () => onMove(t.key ? String(t.key) : undefined),
                                })),
                            }}
                        >
                            Move to Page
                        </ActionButton>
                    )}
                </Stack>
            </Stack>
        </StyleGroupAccordion>

        <StyleGroupAccordion
            label='Title'
            iconName={STYLE_PART_ICONS.heading}
            tooltip='The overlay headline drawn over the slide. Font settings here style that line only.'
            {...group('title')}
        >
            <DynamicSetting id={`storefront.banner.${props.banner.id}.overlayTitle`}>
                <TextField label='Title' value={overlay.title ?? ''} onChange={(_, v) => updateOverlay({ title: v || undefined })} />
            </DynamicSetting>
            <ElementPropertyRows settingKeyPrefix={`storefront.bannerStyle.${props.banner.id}.title`} value={loose(overlay.titleStyle)} onChange={(patch) => updateTitleStyle(loose(patch))} />
            <AdvancedStyleButton onClick={() => setAdvancedGroup('title')} />
        </StyleGroupAccordion>

        <StyleGroupAccordion
            label='Subtitle'
            iconName={STYLE_PART_ICONS.body}
            tooltip='The smaller line under the headline. Font settings here style that line only.'
            {...group('subtitle')}
        >
            <DynamicSetting id={`storefront.banner.${props.banner.id}.overlaySubtitle`}>
                <TextField label='Subtitle' value={overlay.subtitle ?? ''} onChange={(_, v) => updateOverlay({ subtitle: v || undefined })} />
            </DynamicSetting>
            <ElementPropertyRows settingKeyPrefix={`storefront.bannerStyle.${props.banner.id}.subtitle`} value={loose(overlay.subtitleStyle)} onChange={(patch) => updateSubtitleStyle(loose(patch))} />
            <AdvancedStyleButton onClick={() => setAdvancedGroup('subtitle')} />
        </StyleGroupAccordion>

        {/* The only part here that maps to a style element type (button/base), so it's the only
            group with a named-style picker. The Advanced… escape sits AFTER the overrides group: it
            edits the same dict, but it isn't one of the overrides the fold counts, and inside the
            fold it would disappear whenever it's collapsed. */}
        <StyleGroupAccordion
            label='Button'
            iconName={STYLE_PART_ICONS.base}
            tooltip='The call-to-action button on the slide. It only renders when a label is set.'
            {...group('button')}
        >
            <DynamicSetting id={`storefront.banner.${props.banner.id}.buttonLabel`}>
                <TextField
                    label='Label'
                    description='The button only shows when a label is set.'
                    value={overlay.button?.label ?? ''}
                    onChange={(_, v) => updateButton({ label: v || undefined })}
                />
            </DynamicSetting>
            <DynamicSetting id={`storefront.banner.${props.banner.id}.buttonUrl`}>
                <TextField
                    label='URL'
                    placeholder='Defaults to the banner link URL'
                    value={overlay.button?.url ?? ''}
                    onChange={(_, v) => updateButton({ url: v || undefined })}
                />
            </DynamicSetting>
            <StyleOverridesGroup
                pickerLabel='Style'
                rowLayout
                target={styleTargetForKey('buttonStyle')}
                value={overlay.button?.style}
                onChange={updateButtonStyle}
            >
                <ElementPropertyRows settingKeyPrefix={`storefront.bannerStyle.${props.banner.id}.button`} value={overlay.button?.style} onChange={updateButtonStyle} />
            </StyleOverridesGroup>
            <AdvancedStyleButton onClick={() => setAdvancedGroup('button')} />
        </StyleGroupAccordion>

        <StyleGroupAccordion
            label='Box'
            iconName={STYLE_PART_ICONS.band}
            tooltip='Backdrop box around the title, subtitle and button. Padding is the extra space around the text; opacity applies to the background color only, so the text stays solid.'
            {...group('box')}
        >
            <ElementPropertyRows settingKeyPrefix={`storefront.bannerStyle.${props.banner.id}.box`} value={overlay.box} onChange={updateBoxStyle} />
            <StyleRow label='Bg opacity'>
                <TextField
                    type='number'
                    min={0}
                    max={100}
                    suffix='%'
                    placeholder='100'
                    value={overlay.boxOpacity?.toString() ?? ''}
                    onChange={(_, v) => updateOverlay({ boxOpacity: parseNum(v) })}
                    styles={cell.textField}
                />
            </StyleRow>
            <AdvancedStyleButton onClick={() => setAdvancedGroup('box')} />
        </StyleGroupAccordion>

        <StyleGroupAccordion
            label='Position'
            iconName='Move'
            tooltip='Where the overlay stack sits on the slide, and how much of the slide width it may take up.'
            {...group('position')}
        >
            <DynamicSetting id={`storefront.banner.${props.banner.id}.overlayHorizontal`}>
                <StyleRow label='Horizontal'>
                    <Dropdown
                        options={OVERLAY_HORIZONTAL_OPTIONS}
                        selectedKey={overlay.position?.horizontal ?? 'left'}
                        onChange={(_, o) => updatePosition({ horizontal: o?.key as BannerOverlayHorizontal })}
                        styles={cell.dropdown}
                    />
                </StyleRow>
            </DynamicSetting>
            <DynamicSetting id={`storefront.banner.${props.banner.id}.overlayVertical`}>
                <StyleRow label='Vertical'>
                    <Dropdown
                        options={OVERLAY_VERTICAL_OPTIONS}
                        selectedKey={overlay.position?.vertical ?? 'middle'}
                        onChange={(_, o) => updatePosition({ vertical: o?.key as BannerOverlayVertical })}
                        styles={cell.dropdown}
                    />
                </StyleRow>
            </DynamicSetting>
            <DynamicSetting id={`storefront.banner.${props.banner.id}.overlayTextAlign`}>
                <StyleRow label='Text align'>
                    <Dropdown
                        options={OVERLAY_TEXT_ALIGN_OPTIONS}
                        selectedKey={overlay.position?.textAlign ?? ''}
                        onChange={(_, o) => updatePosition({ textAlign: (o?.key as BannerOverlayHorizontal) || undefined })}
                        styles={cell.dropdown}
                    />
                </StyleRow>
            </DynamicSetting>
            <DynamicSetting id={`storefront.banner.${props.banner.id}.overlayMaxWidth`}>
                <StyleRow label='Max width'>
                    <TextField
                        type='number'
                        min={10}
                        max={100}
                        suffix='%'
                        placeholder='50'
                        value={overlay.position?.maxWidth?.toString() ?? ''}
                        onChange={(_, v) => updatePosition({ maxWidth: parseNum(v) })}
                        styles={cell.textField}
                    />
                </StyleRow>
            </DynamicSetting>
        </StyleGroupAccordion>

        {/* Rendered once, outside the accordions: the raw CSS editor for whichever group asked. */}
        {advancedGroup && advanced[advancedGroup] && (
            <StorefrontSectionAdvancedStyleEditorModal
                css={(advanced[advancedGroup].value ?? {}) as Css}
                displayModal
                closer={() => setAdvancedGroup(undefined)}
                onChange={(css) => advanced[advancedGroup].replace(css)}
            />
        )}
        </>
    );
};

export default StorefrontBannerEditorPanel;
